import { AlarmInterval } from '@/graphql/types';
import { minutesToSeconds } from 'date-fns';
import { camelCase } from 'lodash-es';
import type { ChangeEvent } from 'react';
import { useMemo } from 'react';
import { isSomeEnum } from '../../../../utils/is-some-enum';
import { extractSecondsFromLeqInterval } from '../../../../utils/leq-interval-converter';
import Select from '../../../form/Select';
import { getWeekWrappedMinutes } from '../../utils';
import type { ModalFormData } from '../types';
import { TextField } from '../../../modal/fields/TextField';
import type { useAlarmScheduleField } from '../hooks/useAlarmScheduleField';
import { validateLevel } from '../hooks/useModalForm';

interface AlarmScheduleFieldsProps {
    formData: ModalFormData;
    handleChange: (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
    errors: Record<string, string>;
    settings: ReturnType<typeof useAlarmScheduleField>['settings'];
}

const isAlarmInterval = isSomeEnum(AlarmInterval);

export function AlarmScheduleFields({
    formData,
    handleChange,
    errors,
    settings: { lmaxAlarmLevel, leqAlarmLevel, leqInterval, leqTitle },
}: AlarmScheduleFieldsProps) {
    const options = useMemo(() => {
        // At runtime `leqInterval.options` is always defined, but TypeScript cannot
        // infer that because of typing constraints in `measuringPoint/types.ts`.
        if (!leqInterval.options) {
            return [];
        }

        const [startMinutes, endMinutes] = getWeekWrappedMinutes(formData);
        const periodInSeconds = minutesToSeconds(endMinutes - startMinutes);

        const filteredOptions = leqInterval.options.filter(([key]) => {
            if (typeof key !== 'string') {
                return false;
            }

            const intervalInSeconds = extractSecondsFromLeqInterval(camelCase(key));

            /**
             * https://github.com/omnidots/website/pull/8625#issuecomment-3187871572
             * Intervals should be equal or higher to the Leq interval + be able to spread over
             * the whole period. So If I configure a period from 10:00 till 12:30,
             * I should only be able to choose half an hour maximally.
             * This is because the hour will not fit in the period in full increments.
             */
            return (
                isAlarmInterval(key) &&
                intervalInSeconds &&
                intervalInSeconds <= periodInSeconds &&
                periodInSeconds % intervalInSeconds === 0
            );
        });

        if (filteredOptions.length === 0) {
            const defaultOption = leqInterval.options.find(([key]) => key === leqInterval.default);
            if (defaultOption) {
                return [defaultOption];
            }
        }

        return filteredOptions;
    }, [formData, leqInterval]);

    const leqIntervalValue = useMemo(
        () =>
            options.find(([key]) => (key as AlarmInterval) === formData.leqInterval)?.[0] ??
            leqInterval.default,
        [options, leqInterval.default, formData.leqInterval]
    ) as AlarmInterval;

    if (formData.lmaxLevel === undefined) formData.lmaxLevel = 0;
    if (formData.leqLevel === undefined) formData.leqLevel = 0;
    if (!options.find((data) => (data[0] as AlarmInterval) === formData.leqInterval)) {
        formData.leqInterval = leqIntervalValue;
    }

    const isLeqIntervalDisabled = !validateLevel(formData.leqLevel) || formData.leqLevel === 0;

    return (
        <div>
            <div className="mt-2 flex flex-wrap items-start gap-1 rounded-md border-2 border-solid p-2">
                <h3 className="text-md mb-2 w-full">{leqTitle}</h3>
                <TextField
                    error={errors.leqLevel}
                    helperText={leqAlarmLevel.helpText}
                    label={leqAlarmLevel.label}
                    name="leqLevel"
                    formRowClasses="gap-4"
                    onChange={handleChange}
                    value={formData.leqLevel}
                    type="number"
                    step={0.1}
                />
                <div className="flex w-full items-center gap-4">
                    <label htmlFor="form-input-leq-interval" className="whitespace-nowrap text-sm">
                        {leqInterval.label}
                    </label>
                    <Select
                        id="form-input-leq-interval"
                        className="text-sm text-black"
                        data={options}
                        disabled={isLeqIntervalDisabled}
                        onChange={handleChange}
                        name="leqInterval"
                        small
                        value={leqIntervalValue}
                    />
                </div>
            </div>
            <div className="mt-2 rounded-md border-2 border-solid p-2">
                <TextField
                    error={errors.lmaxLevel}
                    helperText={lmaxAlarmLevel.helpText}
                    label={gettext('LMAX_LEVEL')}
                    name="lmaxLevel"
                    onChange={handleChange}
                    value={formData.lmaxLevel}
                    type="number"
                    step={0.1}
                />
            </div>
        </div>
    );
}
